import { useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  createAssumption,
  deleteAssumption,
  editAssumption,
  fetchCashBreakdown,
  fetchCashForecast,
  previewDeleteAssumption,
  recordCashAction,
} from '../../api/cashReport'
import { useCashReportFlag } from '../../hooks/useCashReportFlag'
import { applyOptimisticAssumptions, applyOptimisticBank, buildHeadlines } from './headlines.ts'
import ConclusionBlock from './ConclusionBlock.tsx'
import HeadlineNumbers from './HeadlineNumbers.tsx'
import EquationStrip from './EquationStrip.tsx'
import WeeklyTable from './WeeklyTable.tsx'
import CompanyBreakdownDrawer from './CompanyBreakdownDrawer.tsx'
import CashReportChart from './CashReportChart.tsx'
import ProjectsTable from './ProjectsTable.tsx'
import ProjectCashDrawer from './ProjectCashDrawer.tsx'
import CollectionsList from './CollectionsList.tsx'
import PaymentsList from './PaymentsList.tsx'
import ManualInputs from './ManualInputs.tsx'
import CashOnHandCard from './CashOnHandCard.tsx'
import ActionsBlock from './ActionsBlock.tsx'
import CashReportShareMenu from './CashReportShareMenu.tsx'
import { BrandWordmark } from '../../components/brand/BrandWordmark'
import { CASH_FORECAST_QUERY_KEY, writeForecastVersion } from './applyForecastVersion.ts'
import { emitCash } from './cashReportAnalytics.ts'
import { cashEmptyHeadline, cashEmptyShowConfirmBank, cashEmptyShowSkeleton } from './cashEmptyState.js'
import type { AssumptionRow, CollectionItem, ForecastVersion, PaymentItem, ProjectTableRow, RankedActions } from '../../api/cashReport'
import './cashReport.css'

type SortKey = 'expected_in_cents' | 'expected_out_cents' | 'open_balance_cents' | 'project_name'

export default function CashReportScreen() {
  const { enabled, loading: flagLoading } = useCashReportFlag()
  const qc = useQueryClient()
  const [params, setParams] = useSearchParams()
  const weekParam = params.get('week')
  const projectParam = params.get('project')
  const scopeParam = params.get('scope')
  const [showWeeks, setShowWeeks] = useState(false)
  const [showEngine, setShowEngine] = useState(false)
  const [drawerWeek, setDrawerWeek] = useState<string | null>(weekParam)
  const [drawerScope, setDrawerScope] = useState<string | null>(scopeParam)
  const [drawerProject, setDrawerProject] = useState<number | null>(
    projectParam ? Number(projectParam) : null,
  )
  const [sortKey, setSortKey] = useState<SortKey>('expected_in_cents')
  const [addKind, setAddKind] = useState<string | null>(null)
  const [bankCardOpen, setBankCardOpen] = useState(false)
  const [bankEditId, setBankEditId] = useState<string | null>(null)
  const [bankSaveError, setBankSaveError] = useState(false)
  const [optimisticBank, setOptimisticBank] = useState<{ cents: number; asOf: string } | null>(null)
  const [recomputeMs, setRecomputeMs] = useState<number | null>(null)
  const scrollRef = useRef(0)
  const bankSavingRef = useRef(false)
  const seededVersionRef = useRef<{ version: ForecastVersion; actions?: RankedActions } | null>(null)

  const forecastQ = useQuery({
    queryKey: CASH_FORECAST_QUERY_KEY,
    queryFn: async () => {
      const res = await fetchCashForecast()
      const incoming = res?.data?.version || null
      const seeded = seededVersionRef.current
      if (seeded?.version && incoming) {
        const seedAt = Date.parse(seeded.version.computed_at || '')
        const inAt = Date.parse(incoming.computed_at || '')
        if (Number.isFinite(seedAt) && Number.isFinite(inAt) && seedAt > inAt) {
          return {
            ...res,
            data: {
              ...res.data,
              version: seeded.version,
              ...(seeded.actions !== undefined ? { actions: seeded.actions } : {}),
            },
          }
        }
      }
      if (incoming) seededVersionRef.current = { version: incoming, actions: res?.data?.actions }
      return res
    },
    enabled,
    // Mutation responses seed the cache; avoid focus/mount refetch clobbering
    // a just-written version while the UI settles (shared staging E2E).
    staleTime: 60_000,
    refetchOnWindowFocus: false,
    refetchInterval: (q) => (q.state.data?.data?.pending ? 2000 : false),
  })

  const version = forecastQ.data?.data?.version || null
  const forecastPending = forecastQ.data?.data?.pending === true
  const versionId = version?.id || ''
  const breakdownQ = useQuery({
    queryKey: ['cash-report-breakdown', versionId, drawerWeek, drawerScope],
    queryFn: () => fetchCashBreakdown(versionId, {
      week: drawerWeek || undefined,
      scope: drawerScope === 'opening' ? 'opening' : undefined,
    }),
    enabled: enabled && !!versionId && (!!drawerWeek || drawerScope === 'opening'),
  })
  const projectQ = useQuery({
    queryKey: ['cash-report-project', versionId, drawerProject],
    queryFn: () => fetchCashBreakdown(versionId, { projectId: drawerProject! }),
    enabled: enabled && !!versionId && drawerProject != null,
  })

  useEffect(() => {
    if (weekParam) setDrawerWeek(weekParam)
    if (scopeParam) setDrawerScope(scopeParam)
    if (projectParam) setDrawerProject(Number(projectParam))
  }, [weekParam, projectParam, scopeParam])

  useEffect(() => {
    if (versionId) emitCash('cash_report_viewed', { version_id: versionId })
  }, [versionId])

  function openWeek(week: string | null, key?: string) {
    scrollRef.current = window.scrollY
    setDrawerProject(null)
    const next = new URLSearchParams(params)
    next.delete('project')
    if (key === 'today') {
      setDrawerScope('opening')
      setDrawerWeek(null)
      next.delete('week')
      next.set('scope', 'opening')
    } else {
      if (!week) return
      setDrawerScope(null)
      setDrawerWeek(week)
      next.delete('scope')
      next.set('week', week)
    }
    emitCash('cash_week_opened', { week: key === 'today' ? 'opening' : week || '' })
    setParams(next, { replace: true })
  }

  function openProject(projectId: number, focus?: string | null) {
    scrollRef.current = window.scrollY
    setDrawerWeek(null)
    setDrawerScope(null)
    setDrawerProject(projectId)
    const next = new URLSearchParams(params)
    next.delete('week')
    next.delete('scope')
    next.set('project', String(projectId))
    if (focus) emitCash('cash_source_opened', { source_type: String(focus).split(':')[0], source_id: String(focus).split(':')[1] || String(projectId) })
    setParams(next, { replace: true })
  }

  function closeDrawer() {
    setDrawerWeek(null)
    setDrawerScope(null)
    setDrawerProject(null)
    const next = new URLSearchParams(params)
    next.delete('week')
    next.delete('scope')
    next.delete('project')
    setParams(next, { replace: true })
    requestAnimationFrame(() => window.scrollTo(0, scrollRef.current))
  }

  async function refreshForecast() {
    await qc.refetchQueries({ queryKey: CASH_FORECAST_QUERY_KEY })
  }

  function applyMutationVersion(res: { data?: { version?: unknown; actions?: RankedActions } } | null | undefined) {
    const next = res?.data?.version
    if (!next) throw new Error('Forecast version missing after save')
    // Mutation response is authoritative — do not invalidate/refetch here.
    // A refetch races concurrent user-22 E2E jobs and can overwrite the seeded
    // headline (baseline−1500 → −3000) before the assertion settles.
    seededVersionRef.current = { version: next as ForecastVersion, actions: res?.data?.actions }
    writeForecastVersion(qc, next, 1, { actions: res?.data?.actions })
  }

  async function saveBank(input: {
    kind: 'cash_on_hand'
    amount_cents: number
    effective_date: string
    project_id: null
  }) {
    if (bankSavingRef.current) return
    bankSavingRef.current = true
    setOptimisticBank({ cents: input.amount_cents, asOf: input.effective_date })
    setBankSaveError(false)
    const t0 = performance.now()
    try {
      const res = bankEditId
        ? await editAssumption(bankEditId, input)
        : await createAssumption(input)
      setRecomputeMs(Math.round(performance.now() - t0))
      emitCash(bankEditId ? 'forecast_assumption_edited' : 'forecast_assumption_created', { kind: 'cash_on_hand' })
      applyMutationVersion(res)
      setOptimisticBank(null)
      setBankEditId(null)
    } catch (err) {
      setOptimisticBank(null)
      setBankSaveError(true)
      setBankCardOpen(true)
      throw err
    } finally {
      bankSavingRef.current = false
    }
  }

  function openBankCard(row?: AssumptionRow | null) {
    if (bankSavingRef.current) return
    const list = (forecastQ.data?.data?.version?.payload?.assumptions || []) as AssumptionRow[]
    const existing = row || list.find((a) => a.kind === 'cash_on_hand') || null
    setBankEditId(existing?.id || null)
    setBankSaveError(false)
    setBankCardOpen(true)
  }

  function openSoren(voice: boolean) {
    const next = new URLSearchParams(params)
    next.set('ask', 'cash-report')
    if (voice) next.set('voice', '1')
    else next.delete('voice')
    setParams(next)
  }

  if (flagLoading) return <div className="cr-page"><div className="cr-skel" /><div className="cr-skel" /></div>
  if (!enabled) {
    return (
      <div className="cr-page" data-testid="cr-not-found">
        <h1>Not found</h1>
        <p>This page is not available.</p>
      </div>
    )
  }
  if (forecastQ.error) {
    const status = (forecastQ.error as Error & { status?: number }).status
    return (
      <div className="cr-page" data-testid="cr-error">
        <h1>Company Cash Report</h1>
        <p>{status === 401 ? 'Sign in to view the Cash Report.' : forecastQ.error.message}</p>
      </div>
    )
  }
  const emptyPending = forecastQ.isPending || forecastPending
  if (!version && cashEmptyShowSkeleton({ hasVersion: false, pending: emptyPending })) {
    return (
      <div className="cr-page" data-testid="cr-empty">
        <header className="cr-head">
          <div>
            <BrandWordmark mark={false} />
            <h1>Company Cash Report</h1>
          </div>
        </header>
        <article className="cr-report">
          <div className="cr-report-head">
            <h2>{cashEmptyHeadline({ hasVersion: false, pending: true })}</h2>
          </div>
          <div className="cr-skeleton" data-testid="cr-skeleton">
            <div className="cr-skel" />
            <div className="cr-skel" />
          </div>
        </article>
      </div>
    )
  }
  if (!version && cashEmptyShowConfirmBank({ hasVersion: false, pending: emptyPending })) {
    return (
      <div className="cr-page" data-testid="cr-empty">
        <header className="cr-head">
          <div>
            <BrandWordmark mark={false} />
            <h1>Company Cash Report</h1>
          </div>
        </header>
        <article className="cr-report">
          <div className="cr-report-head">
            <h2>{cashEmptyHeadline({ hasVersion: false, pending: false })}</h2>
          </div>
          <div className="cr-state">
            <button type="button" className="cr-btn dark" data-testid="cr-confirm-bank" onClick={() => openBankCard()}>
              Confirm cash on hand
            </button>
          </div>
        </article>
        <section id="cash-report-cash-on-hand" hidden />
        <CashOnHandCard
          open={bankCardOpen}
          saveError={bankSaveError}
          onClose={() => { setBankCardOpen(false); setBankEditId(null); setBankSaveError(false) }}
          onSave={saveBank}
        />
      </div>
    )
  }
  if (!version) return null

  const headlines = applyOptimisticBank(buildHeadlines(version), optimisticBank)
  const weeks = version.payload?.weeks || []
  const assumptions = applyOptimisticAssumptions(
    (version.payload?.assumptions || []) as AssumptionRow[],
    optimisticBank,
  )
  const editingBank = bankEditId ? assumptions.find((a) => a.id === bankEditId) : null
  const cushion = version.payload?.safety_cushion_cents ?? 2500000
  const projectRows: ProjectTableRow[] = [...(version.payload?.projects_table || [])]
  const sortedProjects = projectRows.sort((a, b) => {
    if (sortKey === 'project_name') return String(a.project_name).localeCompare(String(b.project_name))
    return (Number(b[sortKey]) || 0) - (Number(a[sortKey]) || 0)
  })

  return (
    <div className="cr-page" data-testid="cr-page">
      <header className="cr-head">
        <div>
          <BrandWordmark mark={false} />
          <h1>Company Cash Report</h1>
          <p>
            All active projects · {version.computed_at ? new Date(version.computed_at).toLocaleString() : 'latest version'}
            {forecastPending ? (
              <span className="cr-updating" data-testid="cr-updating"> · updating…</span>
            ) : null}
          </p>
        </div>
        <div className="cr-head-actions">
          <CashReportShareMenu versionId={version.id} />
          <button type="button" className="cr-btn" data-testid="cr-ask-voice" onClick={() => openSoren(true)}>Ask by voice</button>
          <button type="button" className="cr-btn dark" data-testid="cr-ask-soren" onClick={() => openSoren(false)}>Ask Soren</button>
        </div>
      </header>
      <article className="cr-report">
        <ConclusionBlock
          version={version}
          onFix={(code) => {
            if (code === 'bank_stale') {
              openBankCard()
            } else if (code === 'late_receivable') {
              const late = ((version.payload?.collections?.items || []) as CollectionItem[]).find(
                (i) => i.status === 'late',
              )
              if (late?.project_id) openProject(late.project_id, late.focus)
            } else if (code === 'payroll_assumption_stale') {
              setAddKind('payroll_estimate')
              document.getElementById('cash-report-cash-on-hand')?.scrollIntoView({ behavior: 'smooth' })
            }
          }}
        />
        <HeadlineNumbers items={headlines} onOpenWeek={openWeek} onConfirmBank={() => openBankCard()} />
        <p hidden data-testid="cr-recompute-ms">{recomputeMs ?? ''}</p>
        <div className="cr-body">
          <CashReportChart
            weeks={weeks}
            cushionCents={cushion}
            lowestWeek={version.payload?.verdict?.lowest_week}
            onOpenWeek={openWeek}
          />
          <EquationStrip version={version} optimisticBank={optimisticBank} onOpenWeek={openWeek} />
        </div>
        <div className="cr-controls">
          <button type="button" className="cr-btn" aria-expanded={showWeeks} onClick={() => setShowWeeks((v) => !v)}>
            {showWeeks ? 'Hide weekly detail' : 'Show every week'}
          </button>
        </div>
        {showWeeks ? <WeeklyTable weeks={weeks} onOpenWeek={openWeek} /> : null}
      </article>
      <div data-testid="cr-lower-half">
        <ProjectsTable
          rows={sortedProjects}
          sortKey={sortKey}
          onSort={setSortKey}
          onOpen={openProject}
        />
        <div className="cr-report-grid">
          <CollectionsList
            items={(version.payload?.collections?.items || []) as CollectionItem[]}
            total={version.payload?.collections?.total}
            thisWeekStart={weeks[0]?.week_start}
            onOpenProject={openProject}
          />
          <PaymentsList
            items={(version.payload?.payments_list?.items || []) as PaymentItem[]}
            total={version.payload?.payments_list?.total}
            offer={version.payload?.early_pay_offer}
            onOpen={openProject}
          />
        </div>
      </div>
      <ActionsBlock
        top={forecastQ.data?.data?.actions?.top || []}
        more={forecastQ.data?.data?.actions?.more || []}
        onFix={(card) => {
          if (card.card_kind === 'bank_stale') {
            openBankCard()
          }
        }}
        onDone={async (card) => {
          await recordCashAction({ action: 'done', card_kind: card.card_kind, fact_key: card.fact_key })
          emitCash('cash_action_done', { card_kind: card.card_kind })
          await refreshForecast()
        }}
        onRemind={async (card, remindAt) => {
          await recordCashAction({ action: 'remind', card_kind: card.card_kind, fact_key: card.fact_key, remind_at: remindAt })
          emitCash('cash_action_reminded', { card_kind: card.card_kind })
          await refreshForecast()
        }}
        onHide={async (card) => {
          await recordCashAction({ action: 'hide', card_kind: card.card_kind, fact_key: card.fact_key })
          emitCash('cash_action_hidden', { card_kind: card.card_kind })
          await refreshForecast()
        }}
      />
      <CashOnHandCard
        open={bankCardOpen}
        saveError={bankSaveError}
        defaultCents={editingBank?.amount_cents ?? version.opening_cash_cents}
        onClose={() => { setBankCardOpen(false); setBankEditId(null); setBankSaveError(false) }}
        onSave={saveBank}
      />
      <ManualInputs
        rows={assumptions}
        projects={sortedProjects}
        auditCount={forecastQ.data?.data?.audit_count ?? 0}
        startAdd={addKind ? { kind: addKind } : null}
        onConsumedAdd={() => setAddKind(null)}
        onEditBank={(row) => openBankCard(row)}
        onSave={async (input) => {
          const res = input.id
            ? await editAssumption(input.id, input)
            : await createAssumption(input)
          emitCash(input.id ? 'forecast_assumption_edited' : 'forecast_assumption_created', { kind: input.kind })
          applyMutationVersion(res)
        }}
        onDelete={async (id) => {
          const kind = (version.payload?.assumptions || []).find((a) => a.id === id)?.kind || 'unknown'
          const res = await deleteAssumption(id)
          emitCash('forecast_assumption_deleted', { kind })
          applyMutationVersion(res)
        }}
        onPreviewDelete={async (id) => {
          const res = await previewDeleteAssumption(id)
          return res.data
        }}
      />
      <div className="cr-controls">
        <button type="button" className="cr-btn text" aria-expanded={showEngine} onClick={() => setShowEngine((v) => !v)}>
          How this report is calculated
        </button>
      </div>
      {showEngine ? (
        <div className="cr-engine" data-testid="cr-explainer">
          <b>The cash engine calculates this report; Soren explains it.</b> Bank balances,
          approved invoices, predicted receipt dates, vendor due dates, payroll, overhead,
          and confirmed manual assumptions feed the same engine. Derived totals cannot be
          directly edited; open the project or source record that created them.
        </div>
      ) : null}
      <CompanyBreakdownDrawer
        open={!!drawerWeek || drawerScope === 'opening'}
        week={drawerWeek}
        scope={drawerScope === 'opening' ? 'opening' : 'week'}
        versionId={versionId}
        breakdown={breakdownQ.data?.data || null}
        loading={breakdownQ.isPending}
        error={breakdownQ.error ? breakdownQ.error.message : null}
        onClose={closeDrawer}
      />
      <ProjectCashDrawer
        open={drawerProject != null}
        projectId={drawerProject}
        versionId={versionId}
        breakdown={projectQ.data?.data || null}
        loading={projectQ.isPending}
        error={projectQ.error ? projectQ.error.message : null}
        onClose={closeDrawer}
      />
    </div>
  )
}
